import { useState } from "react";
import { motion } from "framer-motion";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { castVote } from "../../lib/ideas";
import { errMsg } from "../../lib/api";

const VoteBtn = ({ active, onClick, up, count, testid }) => {
  const Icon = up ? ThumbsUp : ThumbsDown;
  const on = up ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#00E5FF]" : "border-[#F59E0B] bg-[#F59E0B]/15 text-[#F59E0B]";
  return (
    <motion.button whileTap={{ scale: 0.88 }} onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.12em] transition-colors ${active ? on : "border-cyan-400/15 text-slate-300 hover:border-cyan-400/50 hover:text-white"}`}>
      <Icon className="h-4 w-4" /> {up ? "Want it" : "Skip"} <span className="tabular-nums">{count}</span>
    </motion.button>
  );
};

export const IdeaCard = ({ idea, i, onChange }) => {
  const [busy, setBusy] = useState(false);
  const total = (idea.up || 0) + (idea.down || 0);
  const pct = total ? Math.round(((idea.up || 0) / total) * 100) : 0;

  const vote = async (v) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await castVote(idea.id, idea.my_vote === v ? 0 : v);
      onChange(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <motion.article layout data-testid={`idea-card-${i}`}
      className="flex h-full flex-col rounded-3xl border border-cyan-400/12 bg-[#0F1829] p-6 sm:p-7 hover:border-cyan-400/35 transition-colors duration-500">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
        {idea.source === "team" ? <span className="text-[#00E5FF]">Team idea</span> : <>By {idea.author || "Anonymous angler"}</>}
      </p>
      <h3 className="mt-3 font-display text-2xl font-extrabold uppercase leading-tight text-white break-words">{idea.title}</h3>
      {idea.description && <p className="mt-2 text-sm text-slate-300 whitespace-pre-line break-words">{idea.description}</p>}
      <div className="mt-auto pt-6">
        <div className="mb-2 flex justify-between font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
          <span data-testid={`idea-percent-${i}`}>{total ? `${pct}% want it` : "No votes yet"}</span>
          <span>{total} vote{total === 1 ? "" : "s"}</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-[#F59E0B]/25">
          <motion.div className="h-full rounded-full bg-[#00E5FF]" initial={false} animate={{ width: total ? `${pct}%` : "0%" }} transition={{ duration: 0.6 }} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <VoteBtn up active={idea.my_vote === 1} count={idea.up || 0} onClick={() => vote(1)} testid={`idea-upvote-${i}`} />
          <VoteBtn active={idea.my_vote === -1} count={idea.down || 0} onClick={() => vote(-1)} testid={`idea-downvote-${i}`} />
        </div>
      </div>
    </motion.article>
  );
};
